import { useRef } from "react";

import { useAuth } from "../../hooks/useAuth";
import type { VlaLockPhase } from "../../stores/approvalStore";
import { useDealLock } from "../../stores/approvalStore";
import type { SessionState } from "./coord/messages";

/**
 * Maps the opener's auth + VLA-lock state to the `SessionState` the viewer gates
 * on (OR-22558). Pure so it can be unit-tested without the store/auth hooks.
 *
 * - Unauthenticated → `expired` (the app can't distinguish logout from expiry;
 *   both surface as no session).
 * - A lock the user held that is no longer theirs → `lockLost`. Gated on
 *   `everHeldByMe` so a deal that was read-only *from the start* (held by another
 *   analyst) still counts as `active` — viewing documents read-only is allowed.
 * - Otherwise → `active`.
 * @param {boolean} isAuthenticated - Whether the session is authenticated.
 * @param {VlaLockPhase} lockPhase - The current VLA-lock phase.
 * @param {boolean} everHeldByMe - Whether the user has held the lock this session.
 * @returns {SessionState} The session signal to broadcast.
 */
export function deriveSessionState(
  isAuthenticated: boolean,
  lockPhase: VlaLockPhase,
  everHeldByMe: boolean
): SessionState {
  if (!isAuthenticated) {
    return "expired";
  }
  if (everHeldByMe && lockPhase !== "held-by-me") {
    return "lockLost";
  }
  return "active";
}

/**
 * Live `SessionState` for the opener window, derived from `useAuth` + the VLA
 * lock. Latches "have we ever held the lock" so a later loss reads as `lockLost`
 * while a never-held (read-only) deal stays viewable.
 * @returns {SessionState} The current session signal.
 */
export function useViewerSessionState(): SessionState {
  const { isAuthenticated } = useAuth();
  const { phase } = useDealLock();
  const everHeldByMe = useRef(false);
  if (phase === "held-by-me") {
    everHeldByMe.current = true;
  }
  return deriveSessionState(isAuthenticated, phase, everHeldByMe.current);
}
